import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ColorThemeService } from 'src/app/navigation/services/color-theme-service';
import { CountriesService } from 'src/app/navigation/services/countries-service';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { SimpleDialogData, SimpleAlertDialogComponent } from '../../dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { ExpenseCalculationMapPoint } from '../charts/map-expense-calculation/map-expense-calculation.component';
import { Expense, ExpenseResultDto, ExpenseResultsDto, PackagingSystemExpenseResultsDto, PackagingUnitExpenseResultDto, PackagingUnitExpenseResultsDto } from 'src/app/data-transfer/entities/evaluation-entities/expense-result-dto';
import { TranslocoService } from '@jsverse/transloco';
import { AnalysisParentComponent } from '../analysis-parent/analysis-parent.component';
import { ValidatorApiService } from 'src/app/data-transfer/services/validator-api-service';
import { AuthService } from 'src/app/services/auth-service';
import { PackagingUnitDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-dto';
import { PackagingSystemDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-dto';
import { DecimalPipe } from '@angular/common';
import { ExpenseCalculationCountryDto } from 'src/app/data-transfer/entities/evaluation-entities/expense-calculation-country-dto';
import moment, { locale } from 'moment/min/moment-with-locales';
import { PackagingComponentEntryDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-entry-dto';
import { MultiMaterialCompositeDto } from 'src/app/data-transfer/entities/material-entities/multi-material-composite-dto';

class MappedExpenseResultDto {
  countryName!: string;
  countryCode!: string;
  amount!: number | undefined;
  displayedAmount!: string;
  displayedAmountPerKilo!: string;
  displayedAmountTooltip!: string;
  source?: string;
}
class MappedExpenseResultsDto {
  countryName: string = "";
  countryCode: string = "";
  licenseFeeAmount: number | undefined = 0;
  displayedLicenseFeeAmount: string = "";
  displayedLicenseFeeAmountTooltip: string = "";
  displayedLicenseFeeAmountPerKilo: string = "";
  ecomodulationAmount: number | undefined = 0;
  displayedEcomodulationAmount: string = "";
  displayedEcomodulationAmountTooltip: string = "";
  displayedEcomodulationAmountPerKilo: string = "";
  plasticTaxAmount: number | undefined = 0;
  displayedPlasticTaxAmount: string = "";
  displayedPlasticTaxAmountTooltip: string = "";
  displayedPlasticTaxAmountPerKilo: string = "";
  totalAmount: number | undefined = 0;
  displayedTotalAmount: string = "";
  displayedTotalAmountPerKilo: string = "";
}

export enum DisplayType {
  licenseFee = 0,
  plasticTax = 1,
  total = 2
}

@Component({
  selector: 'app-expense-calculation-html-template',
  templateUrl: './expense-calculation-html-template.component.html',
  styleUrls: ['./expense-calculation-html-template.component.scss']
})
export class ExpenseCalculationHtmlTemplateComponent extends AnalysisParentComponent implements OnInit, OnChanges {

  @Input() packagingPart!: PackagingUnitDto | PackagingSystemDto;
  @Input() expenseResults!: PackagingSystemExpenseResultsDto[] | PackagingUnitExpenseResultsDto[] | undefined;
  @Input() title!: string;
  @Input() public packagingPartType!: number;
  @Input() displayType!: DisplayType;
  @Input() disclaimer?: string;
  @Input() licenseFeeCountries!: ExpenseCalculationCountryDto[];
  @Input() ecomodulationCountries!: ExpenseCalculationCountryDto[];
  @Input() plasticTaxCountries!: ExpenseCalculationCountryDto[];

  chartDataSource!: ExpenseCalculationMapPoint[];

  dataSource: MatTableDataSource<MappedExpenseResultsDto>;
  columns: { key: string, header: string, headerTooltip: string }[];
  displayedColumns: string[] = [];
  selectedRow: MappedExpenseResultsDto | undefined;

  packagingPartWeight!: number;

  toFixedFunc = (number: number) => +number.toFixed(4);

  constructor(
    private countriesService: CountriesService,
    private translateService: TranslocoService,
    private dialog: MatDialog,
    protected validatorApiService: ValidatorApiService,
    protected colorThemeService: ColorThemeService,
    protected authService: AuthService,
    private decimalPipe: DecimalPipe
  ) {
    super(validatorApiService, colorThemeService, authService);
    this.dataSource = new MatTableDataSource<MappedExpenseResultsDto>();
    this.columns = [];
  }

  ngOnInit(): void {
    super.setPackagingPartData(this.packagingPart);
    this.callerLevelId = this.packagingPartType;
  }

  trackByIndex(i: number) { return i; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.expenseResults && this.expenseResults) {
      this.setColumns(this.isPackagingUnit(this.expenseResults[0]));
      this.setWeight(this.expenseResults[0]);
      const mappedExpenseResults = this.mapExpenseResults(this.expenseResults);
      mappedExpenseResults.sort((a,b) => a.countryName.localeCompare(b.countryName));
      this.dataSource.data = mappedExpenseResults;
      this.chartDataSource = this.dataSource.data.filter(x => x.totalAmount != null)
      .map(x => {
        return { code2: x.countryCode, name: x.countryName, value: x.totalAmount ?? 0 }
      });
    }
  }

  private setColumns(isUnit: boolean) {
    const perPackaging = isUnit ? this.translateService.translate('analysis.expenseCalculation.tableColumns.perUnit')
                                : this.translateService.translate('analysis.expenseCalculation.tableColumns.perSystem');

    const perPackagingTooltip = isUnit ? this.translateService.translate('analysis.expenseCalculation.tableColumns.tooltipPerUnit')
                                : this.translateService.translate('analysis.expenseCalculation.tableColumns.tooltipPerSystem');

    const perKg = this.translateService.translate('analysis.expenseCalculation.tableColumns.perKilo');
    const perKgTooltip = this.translateService.translate('analysis.expenseCalculation.tableColumns.tooltipPerKg');

    switch (this.displayType) {
      case DisplayType.licenseFee:
        this.columns = [
          { key: 'countryName', header: this.translateService.translate('analysis.expenseCalculation.tableColumns.countryName'),
            headerTooltip: ""
          },
          { key: 'displayedLicenseFeeAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.licenseFeeAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip
          },
          { key: 'displayedLicenseFeeAmountPerKilo',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.licenseFeeAmount') + " " + perKg,
            headerTooltip: perKgTooltip
          },
          { key: 'displayedEcomodulationAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.ecomodulationAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip
          },
          { key: 'displayedEcomodulationAmountPerKilo',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.ecomodulationAmount') + " " + perKg,
            headerTooltip: perKgTooltip
          },
          { key: 'displayedTotalAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.total'),
            headerTooltip: ""
          }];
          break;
      case DisplayType.plasticTax:
        this.columns = [
          { key: 'countryName', header: this.translateService.translate('analysis.expenseCalculation.tableColumns.countryName'),
            headerTooltip: ""
          },
          { key: 'displayedPlasticTaxAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.plasticTaxAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip,
          },
          { key: 'displayedPlasticTaxAmountPerKilo',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.plasticTaxAmount') + " " + perKg,
            headerTooltip: perKgTooltip

          }];
          break;
      case DisplayType.total:
        this.columns = [
          { key: 'countryName', header: this.translateService.translate('analysis.expenseCalculation.tableColumns.countryName'),
            headerTooltip: ""
          },
          { key: 'displayedLicenseFeeAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.licenseFeeAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip
          },
          { key: 'displayedEcomodulationAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.ecomodulationAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip
          },
          { key: 'displayedPlasticTaxAmount',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.plasticTaxAmount') + " " + perPackaging,
            headerTooltip: perPackagingTooltip
          },
          { key: 'displayedTotalAmount', header: this.translateService.translate('analysis.expenseCalculation.tableColumns.total'),
            headerTooltip: ""
          },
          { key: 'displayedTotalAmountPerKilo',
            header: this.translateService.translate('analysis.expenseCalculation.tableColumns.total') + " " + perKg,
            headerTooltip: perKgTooltip
          },
        ];
        break;
    }

    this.displayedColumns = this.columns.map(x => x.key);
  }

  private isPackagingUnit(result: any): boolean {
    return result.analyzedPackagingUnit != undefined;
  }

  private setWeight(result: any) {
    if (result.analyzedPackagingUnit) {
      this.packagingPartWeight = this.getPackagingUnitWeight(result.analyzedPackagingUnit);
    } else if (result.analyzedPackagingSystem) {
      this.packagingPartWeight = this.getPackagingSystemWeight(result.analyzedPackagingSystem);
    }
  }

  private getPackagingSystemWeight(packagingSystem: PackagingSystemDto): number {
    return this.getSystemWeightRecursively(packagingSystem);
  }

  private getSystemWeightRecursively(packagingSystem: PackagingSystemDto, quantity = 1): number {
    let totalWeight = 0;
    packagingSystem.packagingUnits.forEach(unit => {
      totalWeight += this.getPackagingUnitWeight(unit.underlyingPackagingUnit) * unit.quantity;
    });
    for (const subSystem of packagingSystem.packagingSystems) {
      totalWeight += this.getSystemWeightRecursively(subSystem.underlyingPackagingSystem, subSystem.quantity);
    }
    return totalWeight * quantity;
  }

  private getPackagingUnitWeight(packagingUnit: PackagingUnitDto): number {
    const weightCalculator = (components: PackagingComponentEntryDto[]) => {
      let totalWeight = 0;
      components.forEach(x => totalWeight += this.getMaterialWeightRecursively(x.underlyingComponent.multiMaterial))
      return totalWeight;
    };
    const mainBodyWeight = packagingUnit.mainBody ? weightCalculator([packagingUnit.mainBody]) : 0;
    const closuresWeight = weightCalculator(packagingUnit.closures);
    const labelsWeight = weightCalculator(packagingUnit.labels);
    const sleevesWeight = weightCalculator(packagingUnit.sleeves);
    const inMoldLabelsWeight = weightCalculator(packagingUnit.inMoldLabels);
    const inlaysWeight = weightCalculator(packagingUnit.inlays);
    const packAidsWeight = weightCalculator(packagingUnit.packagingAids);
    return mainBodyWeight + closuresWeight + labelsWeight + sleevesWeight + inMoldLabelsWeight + inlaysWeight + packAidsWeight;
  }

  private getMaterialWeightRecursively(multiMaterial: MultiMaterialCompositeDto): number {
    let totalWeight = multiMaterial.totalWeight ?? 0;
    for (const subMultiMaterial of multiMaterial.subMultiMaterials) {
      totalWeight += this.getMaterialWeightRecursively(subMultiMaterial);
    }
    return totalWeight;
  }

  private updateTotalExpense(totalExpense: Expense | null, expense: Expense | null): Expense | null {
    if (expense == null) {
      if (totalExpense == null) {
        return null;
      }

      return totalExpense;
    }

    if (totalExpense == null) {
      totalExpense = new Expense();
      totalExpense.amount = expense.amount;
      totalExpense.currency = expense.currency;
      totalExpense.informationSource = expense.informationSource;
    } else {
      totalExpense.amount += expense.amount;
    }

    return totalExpense;
  }

  private mapExpenseResults(expenseResults: PackagingSystemExpenseResultsDto[] | ExpenseResultsDto<PackagingUnitExpenseResultDto>[])
      : MappedExpenseResultsDto[] {
    const mappedResults = [];

    for (const expenseResult of expenseResults) {
      const mappedExpenseResult = new MappedExpenseResultsDto();
      let totalExpense: Expense | null = null;
      if ((this.displayType == DisplayType.licenseFee || this.displayType == DisplayType.total) && expenseResult.licenseFee) {
        const mappedLicenseFeeResult
          = this.mapExpenseResult(expenseResult.licenseFee, x => this.getReasonForMissingExpense(x, this.licenseFeeCountries));
        mappedExpenseResult.countryCode = mappedLicenseFeeResult.countryCode;
        mappedExpenseResult.countryName = mappedLicenseFeeResult.countryName;
        mappedExpenseResult.licenseFeeAmount = mappedLicenseFeeResult.amount;
        mappedExpenseResult.displayedLicenseFeeAmount = mappedLicenseFeeResult.displayedAmount;
        mappedExpenseResult.displayedLicenseFeeAmountPerKilo = mappedLicenseFeeResult.displayedAmountPerKilo;
        mappedExpenseResult.displayedLicenseFeeAmountTooltip = mappedLicenseFeeResult.displayedAmountTooltip;

        totalExpense = this.updateTotalExpense(totalExpense, expenseResult.licenseFee.expense);
      }

      if ((this.displayType == DisplayType.licenseFee || this.displayType == DisplayType.total) && expenseResult.ecomodulation) {
        const mappedEcomodulationResult
          = this.mapExpenseResult(expenseResult.ecomodulation, x => this.getReasonForMissingExpense(x, this.ecomodulationCountries));
        mappedExpenseResult.countryCode = mappedEcomodulationResult.countryCode;
        mappedExpenseResult.countryName = mappedEcomodulationResult.countryName;
        mappedExpenseResult.ecomodulationAmount = mappedEcomodulationResult.amount;
        mappedExpenseResult.displayedEcomodulationAmount = mappedEcomodulationResult.displayedAmount;
        mappedExpenseResult.displayedEcomodulationAmountPerKilo = mappedEcomodulationResult.displayedAmountPerKilo;
        mappedExpenseResult.displayedEcomodulationAmountTooltip = mappedEcomodulationResult.displayedAmountTooltip;

        totalExpense = this.updateTotalExpense(totalExpense, expenseResult.ecomodulation.expense);
      }

      if ((this.displayType == DisplayType.plasticTax || this.displayType == DisplayType.total) && expenseResult.plasticTax) {
        const mappedPlasticTaxResult
          = this.mapExpenseResult(expenseResult.plasticTax, x => this.getReasonForMissingExpense(x, this.plasticTaxCountries));
        mappedExpenseResult.countryCode = mappedPlasticTaxResult.countryCode;
        mappedExpenseResult.countryName = mappedPlasticTaxResult.countryName;
        mappedExpenseResult.plasticTaxAmount = mappedPlasticTaxResult.amount;
        mappedExpenseResult.displayedPlasticTaxAmount = mappedPlasticTaxResult.displayedAmount;
        mappedExpenseResult.displayedPlasticTaxAmountPerKilo = mappedPlasticTaxResult.displayedAmountPerKilo;
        mappedExpenseResult.displayedPlasticTaxAmountTooltip = mappedPlasticTaxResult.displayedAmountTooltip;

        totalExpense = this.updateTotalExpense(totalExpense, expenseResult.plasticTax.expense);
      }

      const totalResult = new ExpenseResultDto();
      totalResult.evaluatedCountry = mappedExpenseResult.countryCode;
      totalResult.expense = totalExpense;
      const mappedTotalExpenseResult = this.mapExpenseResult(totalResult, _ => ["--", ""]);
      mappedExpenseResult.totalAmount = mappedTotalExpenseResult.amount;
      mappedExpenseResult.displayedTotalAmount = mappedTotalExpenseResult.displayedAmount;
      mappedExpenseResult.displayedTotalAmountPerKilo = mappedTotalExpenseResult.displayedAmountPerKilo;

      mappedResults.push(mappedExpenseResult);
    }

    return mappedResults;
  }

  private getReasonForMissingExpense(expenseResult: ExpenseResultDto,
                                     expenseCalculationCountries: ExpenseCalculationCountryDto[]): [string, string] {
    const expenseCalculationCountry = expenseCalculationCountries.find(x => expenseResult.evaluatedCountry == x.countryCode);

    if (expenseCalculationCountry == null || !expenseCalculationCountry.hasExpense) {
      return [this.translateService.translate('analysis.expenseCalculation.missingResult.noFee'),
              this.translateService.translate('analysis.expenseCalculation.missingResult.tooltips.noFee')];
    } else {
      if (expenseCalculationCountry.isIncluded) {
        return [this.translateService.translate('analysis.expenseCalculation.missingResult.includedInErp'),
                this.translateService.translate('analysis.expenseCalculation.missingResult.tooltips.includedInErp')];
      }

      if (expenseCalculationCountry.hasExpenseFrom == null) {
        return [this.translateService.translate('analysis.expenseCalculation.missingResult.planned'),
                this.translateService.translate('analysis.expenseCalculation.missingResult.tooltips.planned')];
      }

      const evaluationReferenceTimestamp = moment(expenseResult.evaluationReferenceTimestamp);
      const plannedFor = moment(expenseCalculationCountry.hasExpenseFrom);

      if (plannedFor.isAfter(evaluationReferenceTimestamp)) {
        const year = plannedFor.format("YYYY");
        return [this.translateService.translate('analysis.expenseCalculation.missingResult.plannedFor') + " " + year,
                this.translateService.translate('analysis.expenseCalculation.missingResult.tooltips.plannedFor') + " " + year];
      } else {
        return [this.translateService.translate('analysis.expenseCalculation.missingResult.inDevelopment'),
                this.translateService.translate('analysis.expenseCalculation.missingResult.tooltips.inDevelopment')];
      }
    }
  }

  private mapExpenseResult(expenseResult: ExpenseResultDto, missingExpenseProvider: (expenseResult: ExpenseResultDto) => [string, string])
  : MappedExpenseResultDto {
    const isExpenseCalculated = expenseResult.expense != null;
    const amountNotRounded = expenseResult.expense?.amount;
    const amount = amountNotRounded != undefined ? this.toFixedFunc(amountNotRounded) : undefined;
    const packagingWeight = this.packagingPartWeight != null && this.packagingPartWeight !== 0 ? this.packagingPartWeight : 1;
    const amountPerKilo = amountNotRounded != undefined ? this.toFixedFunc(amountNotRounded / (packagingWeight / 1000)) : undefined;

    let currencySymbol = expenseResult.expense?.currency.symbol;
    if (currencySymbol == null) {
      currencySymbol = expenseResult.evaluatedCountry === 'GB' ? '£' : '€';
    }

    let displayedAmount = "";
    let displayedAmountPerKilo = "";
    let displayedAmountTooltip = "";

    if (isExpenseCalculated) {
      displayedAmount = `${this.decimalPipe.transform(amount, '1.4-4')} ${currencySymbol}`;
      displayedAmountPerKilo = `${this.decimalPipe.transform(amountPerKilo, '1.4-4')} ${currencySymbol}`;
    } else {
      [displayedAmount, displayedAmountTooltip] = missingExpenseProvider(expenseResult);
      displayedAmountPerKilo = "--";
    }

    const mappedResult =
      {
        countryCode: expenseResult.evaluatedCountry,
        countryName: this.getTranslatedCountryCode(expenseResult.evaluatedCountry),
        amount: amount,
        displayedAmount,
        displayedAmountPerKilo,
        displayedAmountTooltip,
        source: expenseResult.expense?.informationSource
      };

    return mappedResult;
  }

  private getTranslatedCountryCode(countryCode: string) {
    const translatedName = this.countriesService.getCountries().find(y => y.code === countryCode)?.name;
    return translatedName ?? countryCode;
  }

  countryClicked(countryCode: string) {
    const expenseResult = this.dataSource.data.find(x => x.countryCode === countryCode);
    if (!expenseResult) { return; }
    this.selectedRow = expenseResult;
    const dialogData: SimpleDialogData = {
      title: expenseResult.countryName,
      messages: [
        `${this.translateService.translate('analysis.expenseCalculation.tableColumns.total')}: ${expenseResult.displayedTotalAmount}`,
        `${this.translateService.translate('analysis.expenseCalculation.tableColumns.total')} ${this.translateService.translate('analysis.expenseCalculation.tableColumns.perKilo')}:
        ${expenseResult.displayedTotalAmountPerKilo}`],
      icon: 'info'
    }
    const dialogConfig: MatDialogConfig = getDialogConfig(dialogData);
    this.dialog.open(SimpleAlertDialogComponent, dialogConfig)
  }
}
